import { Suspense, lazy, useEffect, useMemo, useRef, type ReactNode } from "react";
import { decodePairingCode, isPairingExpired, type PairingPayload } from "@foreman/protocol";
import { track } from "./analytics";
import type { Services } from "./services";
import { useNav, type Nav, type View } from "./nav";
import { AppContext, ReconnectNotice, Toast, useToast, useViewport } from "./ui";
import { ComputersScreen } from "./screens/Computers";
import { HomeScreen } from "./screens/Home";
import { SignInScreen, useSigned } from "./screens/SignIn";
import { AccountScreen } from "./screens/Account";
import { HeadsUp } from "./heads-up";
import { askSignIn, SignInSheet } from "./gate";
import { Preview } from "./screens/Upgrade";
import { BackButton, useConnection } from "./ui";
import { can } from "@foreman/protocol";
import { fetchAccount, linkCodeFromUrl } from "./account";
import { InboxScreen } from "./screens/Inbox";
import { TeamsScreen } from "./screens/Teams";
import { OPEN_INBOX_EVENT } from "./notifications";
import { alreadyPaired, confirmViewFor, handedOverPairing, redeemHandedOverSignIn, startSignInHandoff } from "./pair-handoff";
import { installedApp } from "./install-hint";
import { AlertsAsk } from "./alerts-ask";
import { ReviewAsk } from "./review-ask";
import { OPEN_ALERT_EVENT, alertFromUrl, openAlert, type AlertTarget } from "./alert-open";
import { useAttention } from "./attention";
// Only Home, the tabs and sign-in are in the first download; every other screen loads when it is first opened (and is
// fetched quietly in the background once the app is up), so the app opens sooner, which matters most on an iPhone cold start.
const screenImports = {
  pair: () => import("./screens/Pair"),
  sessions: () => import("./screens/Sessions"),
  devices: () => import("./screens/Devices"),
  diagnostics: () => import("./screens/Diagnostics"), // warmed with the rest: it must open even when the network is down
  changes: () => import("./screens/Changes"),
  branches: () => import("./screens/Branches"),
  history: () => import("./screens/History"),
  earlier: () => import("./screens/Earlier"),
  link: () => import("./screens/Link"),
  support: () => import("./screens/Support"),
  rules: () => import("./screens/Rules"),
  usage: () => import("./screens/Usage"),
  skills: () => import("./screens/Skills"),
};
const ScanScreen = lazy(() => screenImports.pair().then((m) => ({ default: m.ScanScreen })));
const InstallFirstScreen = lazy(() => screenImports.pair().then((m) => ({ default: m.InstallFirstScreen })));
const ConfirmScreen = lazy(() => screenImports.pair().then((m) => ({ default: m.ConfirmScreen })));
const PairingScreen = lazy(() => screenImports.pair().then((m) => ({ default: m.PairingScreen })));
const SessionsScreen = lazy(() => screenImports.sessions().then((m) => ({ default: m.SessionsScreen })));
const DevicesScreen = lazy(() => screenImports.devices().then((m) => ({ default: m.DevicesScreen })));
const DiagnosticsScreen = lazy(() => screenImports.diagnostics().then((m) => ({ default: m.DiagnosticsScreen })));
const SupportScreen = lazy(() => screenImports.support().then((m) => ({ default: m.SupportScreen })));
const ChangesScreen = lazy(() => screenImports.changes().then((m) => ({ default: m.ChangesScreen })));
const BranchesScreen = lazy(() => screenImports.branches().then((m) => ({ default: m.BranchesScreen })));
const HistoryScreen = lazy(() => screenImports.history().then((m) => ({ default: m.HistoryScreen })));
const EarlierScreen = lazy(() => screenImports.earlier().then((m) => ({ default: m.EarlierScreen })));
const RulesScreen = lazy(() => screenImports.rules().then((m) => ({ default: m.RulesScreen })));
const SkillsScreen = lazy(() => screenImports.skills().then((m) => ({ default: m.SkillsScreen })));
const UsageScreen = lazy(() => screenImports.usage().then((m) => ({ default: m.UsageScreen })));
const LinkScreen = lazy(() => screenImports.link().then((m) => ({ default: m.LinkScreen })));
/** Warm the other screens (and the terminal) once the first screen has painted and the phone is idle. */
function prefetchScreens() {
  const run = () => {
    for (const load of Object.values(screenImports)) void load().catch(() => {});
    void import("./screens/Terminal").catch(() => {});
  };
  if ("requestIdleCallback" in window) (window as Window & typeof globalThis).requestIdleCallback(run, { timeout: 4000 });
  else setTimeout(run, 2500);
}
// The terminal (xterm.js) is the heaviest part and only needed once a session is opened, so it loads on demand.
const TerminalScreen = lazy(() => import("./screens/Terminal").then((m) => ({ default: m.TerminalScreen })));

/** A pairing link (`https://app/#pair=FM1...`, what a phone camera opens from the QR code) starts at the confirm step. */
let coldAlert: AlertTarget | null = null; // the alert whose tap opened the app, handled once the app is up

function initialView(): View {
  const link = linkCodeFromUrl(location.search); // a computer sent us here to sign in
  if (link) return { name: "link", code: link };
  if (new URLSearchParams(location.search).get("open") === "inbox") {
    coldAlert = alertFromUrl(location.search);
    history.replaceState(null, "", location.pathname + location.hash); // a tapped alert opened the app cold: land on the inbox
    return { name: "inbox" };
  }
  const m = /pair=(FM1\.[A-Za-z0-9_-]+)/.exec(location.hash);
  let fromHash: PairingPayload | null = null;
  if (m) {
    try {
      const payload = decodePairingCode(location.hash); // the whole hash, so a name next to the code comes along
      fromHash = payload;
      const view = confirmViewFor(payload);
      // On an iPhone browser tab the code stays in the address: the Home Screen app made from this page starts from it.
      if (view.name !== "installfirst") history.replaceState(null, "", location.pathname + location.search); // otherwise don't leave the one-time code in the address bar
      if (!isPairingExpired(payload) && !(installedApp() && alreadyPaired(payload))) return view;
    } catch {
      /* fall through to the normal start */
    }
  }
  // The installed app's first launch: the code left for it by the page it was added from (its address, or the cookie).
  const handed = handedOverPairing(fromHash);
  if (handed) {
    track("pair_handoff_resumed");
    return { name: "confirm", payload: handed };
  }
  return { name: "home" };
}

/**
 * Source control and earlier messages are for signed-in phones. A guest who opens one is asked to sign in right there (the
 * sheet), and the screen carries on once they have; "Not now" goes back. Until the computer has said what this phone is, and
 * for any phone that is signed in here, nothing is shown but the neutral screen: a signed-in phone never sees a sign-in ask.
 */
function PlanGate({ nav, view, children }: { nav: Nav; view: View; children: ReactNode }) {
  const wanted = view.name === "changes" || view.name === "branches" || view.name === "history" ? "git" : view.name === "earlier" ? "history.conversation" : null;
  const computerId = "computerId" in view ? view.computerId : "";
  const { snap } = useConnection(computerId);
  const signed = useSigned();
  const plan = snap.account?.plan;
  const allowed = plan === undefined ? true : can({ plan }, wanted ?? "git");
  const hold = !!wanted && !allowed && !!signed; // signed in here, computer not caught up yet: wait, never ask
  const asks = !!wanted && !allowed && !signed;
  const key = `${view.name}:${computerId}`;
  const back = useRef(nav.back);
  back.current = nav.back;
  useEffect(() => {
    if (!asks) return;
    // closing the sheet without signing in (Not now, or a tap outside it) leaves this screen: there is nothing here for a guest
    askSignIn({ trigger: wanted === "git" ? "git" : "history", computerId, resume: undefined, onDismiss: () => back.current() });
  }, [asks, key, wanted, computerId]);
  if (hold) return <div className="screen" />;
  if (asks) {
    // the feature drawn from example rows, blurred, behind the sheet, so it is clear what signing in unlocks (and never a blank screen)
    return (
      <div className="screen" data-screen="gated">
        <div className="safe-top">
          <div className="topbar">
            <BackButton onClick={nav.back} />
          </div>
        </div>
        <div className="scroll">
          <div className="content" style={{ padding: "16px 16px 32px" }}>
            <div className="gated">
              <div className="gated-back">
                <Preview feature={wanted === "git" ? "git" : "history.conversation"} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

export function App({ services }: { services: Services }) {
  const nav = useNav(useMemo(initialView, []));
  const toast = useToast();
  const ctx = useMemo(() => ({ ...services, toast: toast.show }), [services, toast.show]);
  const { keyboardOpen } = useViewport();
  useAttention(services.hub);
  useEffect(prefetchScreens, []);

  // A signed-in phone hands its sign-in to every computer, so they link themselves to the account. Signing out stops that.
  const signed = useSigned();
  useEffect(() => {
    services.hub.setAccountToken(signed?.token ?? null);
    if (signed) void fetchAccount(); // a sign-in the relay no longer knows (ended, suspended) signs this phone out
  }, [signed, services.hub]);

  // Tapping an alert brings the app forward and lands on the inbox.
  const viewName = nav.view.name;
  useEffect(() => {
    const open = () => viewName !== "inbox" && nav.goTab({ name: "inbox" });
    window.addEventListener(OPEN_INBOX_EVENT, open);
    return () => window.removeEventListener(OPEN_INBOX_EVENT, open);
  }, [viewName, nav]);

  // ...and to the exact card or session the alert was about (also when the tap is what opened the app).
  const navRef = useRef(nav);
  navRef.current = nav;
  useEffect(() => {
    let stop: (() => void) | null = null;
    const go = (t: AlertTarget) => {
      stop?.();
      stop = openAlert(services.hub, navRef.current, t);
    };
    const on = (e: Event) => go((e as CustomEvent<AlertTarget>).detail);
    window.addEventListener(OPEN_ALERT_EVENT, on);
    if (coldAlert) (go(coldAlert), (coldAlert = null));
    return () => {
      window.removeEventListener(OPEN_ALERT_EVENT, on);
      stop?.();
    };
  }, [services.hub]);

  // An iPhone's Home Screen app has storage of its own: a browser tab that is signed in leaves a one-time code on its address, and the
  // app's first launch signs itself in with it. (In a browser tab this keeps a fresh code there for the moment someone adds the app.)
  useEffect(() => {
    if (installedApp()) {
      void redeemHandedOverSignIn();
      return;
    }
    return startSignInHandoff();
  }, []);

  // A computer that disappears (removed here or elsewhere) must not leave us on one of its screens.
  const v = nav.view;
  const computerId = "computerId" in v ? v.computerId : null;
  const gone = computerId !== null && !services.store.get(computerId);
  useEffect(() => {
    if (gone) nav.home();
  }, [gone, nav]);

  let screen: ReactNode;
  switch (v.name) {
    case "home":
      screen = <HomeScreen nav={nav} />;
      break;
    case "computers":
      screen = <ComputersScreen nav={nav} />;
      break;
    case "inbox":
      screen = <InboxScreen nav={nav} />;
      break;
    case "teams":
      screen = <TeamsScreen nav={nav} />;
      break;
    case "account":
      screen = <AccountScreen nav={nav} />;
      break;
    case "usage":
      screen = <UsageScreen nav={nav} forComputer={v.forComputer} />;
      break;
    case "skills":
      screen = <SkillsScreen nav={nav} forComputer={v.forComputer} folder={v.folder} />;
      break;
    case "rules":
      screen = <RulesScreen nav={nav} forComputer={v.forComputer} />;
      break;
    case "scan":
      screen = <ScanScreen nav={nav} />;
      break;
    case "confirm":
      screen = <ConfirmScreen nav={nav} payload={v.payload} />;
      break;
    case "installfirst":
      screen = <InstallFirstScreen nav={nav} payload={v.payload} />;
      break;
    case "pairing":
      screen = <PairingScreen nav={nav} payload={v.payload} phoneName={v.phoneName} computerName={v.computerName} />;
      break;
    case "sessions":
      screen = gone ? null : <SessionsScreen key={v.computerId} nav={nav} computerId={v.computerId} openNew={v.newSession} />;
      break;
    case "devices":
      screen = gone ? null : <DevicesScreen nav={nav} computerId={v.computerId} />;
      break;
    case "diagnostics":
      screen = gone ? null : <DiagnosticsScreen nav={nav} computerId={v.computerId} />;
      break;
    case "support":
      screen = <SupportScreen nav={nav} topic={v.topic} diag={v.diag} />;
      break;
    case "signin":
      screen = <SignInScreen nav={nav} then={v.then} required={v.required} />;
      break;
    case "link":
      screen = <LinkScreen nav={nav} code={v.code} />;
      break;
    case "changes":
      screen = gone ? null : <ChangesScreen key={`${v.sessionId}\0${v.against ?? ""}`} nav={nav} computerId={v.computerId} sessionId={v.sessionId} against={v.against} />;
      break;
    case "branches":
      screen = gone ? null : <BranchesScreen nav={nav} computerId={v.computerId} sessionId={v.sessionId} />;
      break;
    case "history":
      screen = gone ? null : <HistoryScreen nav={nav} computerId={v.computerId} sessionId={v.sessionId} />;
      break;
    case "earlier":
      screen = gone ? null : <EarlierScreen nav={nav} computerId={v.computerId} sessionId={v.sessionId} />;
      break;
    case "terminal":
      screen = gone ? null : (
        <Suspense fallback={<div className="term-screen" />}>
          <TerminalScreen key={v.sessionId} nav={nav} computerId={v.computerId} sessionId={v.sessionId} />
        </Suspense>
      );
      break;
  }

  return (
    <AppContext.Provider value={ctx}>
      <div className={"app" + (keyboardOpen ? " kb" : "")}>
        <div className="stage">
          <Suspense fallback={<div className="screen" />}>
            <PlanGate nav={nav} view={v}>{screen}</PlanGate>
          </Suspense>
          <HeadsUp nav={nav} />
          <SignInSheet hub={services.hub} />
          <AlertsAsk notifications={services.notifications} view={v} />
          <ReviewAsk view={v} />
          <ReconnectNotice hub={services.hub} />
          <Toast message={toast.message} />
        </div>
      </div>
    </AppContext.Provider>
  );
}
